import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/Label";
import { useUploadMedia } from "@/lib/useUploadMedia";
import {
  cn,
  convertPriceFromCent,
  convertPriceToCent,
  extractTimeFromDateTime,
} from "@/lib/utils";
import {
  MenuFormula,
  MenuFormulaFormData,
} from "@/redux/services/menu/menu_formula.type";
import {
  useCreateMenuFormulaMutation,
  useUpdateMenuFormulaMutation,
} from "@/redux/services/menu/menu_formula.api";
import toast from "react-hot-toast";
import { logger } from "@/services/Logger";
import { ChevronDown } from "lucide-react";
import { toastApiError } from "@/lib/apiErrors";

const DAY_LABELS = [
  { value: 0, label: "Dim" },
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mer" },
  { value: 4, label: "Jeu" },
  { value: 5, label: "Ven" },
  { value: 6, label: "Sam" },
];

const Field = ({ children }: { children: React.ReactNode }) => (
  <div className="[&>label]:block [&>label]:mb-1 [&>label]:text-sm [&>label]:text-card-foreground">
    {children}
  </div>
);

const Section = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

interface FormulaFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  formula?: MenuFormula;
  onSuccess?: () => void;
}

const FormulaFormDrawer: React.FC<FormulaFormDrawerProps> = ({
  isOpen,
  onClose,
  formula,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0);
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [createFormula, { isLoading: isCreating }] =
    useCreateMenuFormulaMutation();
  const [updateFormula, { isLoading: isUpdating }] =
    useUpdateMenuFormulaMutation();
  const { uploadMedia } = useUploadMedia();

  const isEditing = !!formula;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (formula) {
      setName(formula.name);
      setDescription(formula.description ?? "");
      setPrice(convertPriceFromCent(formula.price));
      setSortOrder(formula.sortOrder);
      setIsActive(formula.isActive);
      setDaysOfWeek(formula.daysOfWeek ?? []);
      setStartTime(
        formula.startTime ? extractTimeFromDateTime(formula.startTime) : ""
      );
      setEndTime(
        formula.endTime ? extractTimeFromDateTime(formula.endTime) : ""
      );
      setImagePreview(formula.image?.url ?? "");
      setImageFile(null);
    } else {
      setName("");
      setDescription("");
      setPrice(0);
      setSortOrder(0);
      setIsActive(true);
      setDaysOfWeek([]);
      setStartTime("");
      setEndTime("");
      setImagePreview("");
      setImageFile(null);
    }
  }, [formula, isOpen]);

  const toggleDay = (day: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validateForm = (): boolean => {
    if (!name.trim()) {
      toast.error("Le nom de la formule est requis");
      return false;
    }
    if (price <= 0) {
      toast.error("Le prix doit être supérieur à zéro");
      return false;
    }
    if (isActive && daysOfWeek.length === 0) {
      toast.error("Sélectionnez au moins un jour de disponibilité");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      let imagePath = formula?.image
        ? `/api/media_objects/${formula.image.id}`
        : undefined;

      if (imageFile) {
        const uploadedMedia = await uploadMedia(imageFile);
        if (uploadedMedia) {
          imagePath = `/api/media_objects/${uploadedMedia.id}`;
        }
      }

      const data: MenuFormulaFormData = {
        name,
        description: description || undefined,
        price: convertPriceToCent(price),
        sortOrder,
        isActive,
        daysOfWeek,
        startTime: startTime ? `${startTime}:00` : null,
        endTime: endTime ? `${endTime}:00` : null,
        ...(imagePath && { image: imagePath }),
      };

      if (isEditing && formula) {
        await updateFormula({ id: formula.id, data }).unwrap();
        toast.success("Formule mise à jour avec succès");
      } else {
        await createFormula(data).unwrap();
        toast.success("Formule créée avec succès");
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error("FormulaFormDrawer", "Error saving formula:", error);
      toastApiError(error, "Erreur lors de l'enregistrement de la formule");
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <form
          onSubmit={handleSubmit}
          className="flex flex-col h-full min-h-0"
        >
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
            <SheetTitle className="text-base">
              {isEditing ? "Modifier la formule" : "Créer une formule"}
            </SheetTitle>
            <SheetDescription hidden>
              Formulaire de création ou modification d'une formule.
            </SheetDescription>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
            <Section title="Informations">
              <Field>
                <Label label="Image" htmlFor="formula-image" />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Aperçu"
                    className="w-20 h-20 object-cover rounded mt-1 mb-2"
                  />
                )}
                <input
                  type="file"
                  id="formula-image"
                  className="block w-full text-sm border bg-white rounded-lg py-2 px-2 mt-1"
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </Field>
              <Field>
                <Input
                  label="Nom"
                  type="text"
                  id="formula-name"
                  placeholder="Nom de la formule"
                  required
                  className="h-10 py-2 text-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field>
                <TextArea
                  label="Description"
                  placeholder="Description de la formule"
                  id="formula-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
              <Field>
                <Input
                  label="Prix"
                  type="number"
                  id="formula-price"
                  placeholder="Prix"
                  min={0}
                  step="0.01"
                  required
                  className="h-10 py-2 text-sm"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                />
              </Field>
              <Field>
                <Input
                  label="Position"
                  type="number"
                  id="formula-sortOrder"
                  placeholder="Position"
                  min={0}
                  className="h-10 py-2 text-sm"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(parseInt(e.target.value) || 0)
                  }
                />
              </Field>
            </Section>

            <Section title="Disponibilité">
              <Field>
                <Label label="Jours de disponibilité" />
                <div className="flex flex-wrap gap-2 mt-1">
                  {DAY_LABELS.map(({ value, label }) => {
                    const isSelected = daysOfWeek.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleDay(value)}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-primary/30"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <Input
                    label="Heure de début"
                    type="time"
                    id="formula-startTime"
                    className="h-10 py-2 text-sm"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </Field>
                <Field>
                  <Input
                    label="Heure de fin"
                    type="time"
                    id="formula-endTime"
                    className="h-10 py-2 text-sm"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </Field>
              </div>
              <p className="text-xs text-muted-foreground">
                Laissez vide pour disponibilité toute la journée.
              </p>

              <div className="flex items-start gap-2.5 pt-1">
                <Checkbox
                  id="formula-isActive"
                  className="mt-0.5"
                  checked={isActive}
                  onCheckedChange={(checked) => setIsActive(checked as boolean)}
                />
                <Label
                  htmlFor="formula-isActive"
                  label="Formule active"
                  id="formula-isActive"
                  className="cursor-pointer"
                />
              </div>
            </Section>
          </div>

          <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? "Mettre à jour" : "Créer"}
            </Button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default FormulaFormDrawer;
